import type { ContentCard } from '@braze/react-native-sdk'
import { DesignSystem } from '..'
import { useCallback, useEffect } from 'react'
import * as cardMetrics from '@/src/services/Braze/contentCardMetrics'
import { useMediaQuery } from 'react-responsive'
import { TRANSITION_TO_SPLIT_SCREEN_WIDTH } from './types'
import { StyleSheet, type ViewProps } from 'react-native'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { Image } from 'expo-image'
import { useLogError } from '@/src/common/hooks/useLogError'
import { navigateToAppUrl } from './navigateToAppUrl'

const styles = StyleSheet.create({
  card: { flex: 1 },
  cardHeader: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingVertical: 32,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 16,
  },
  image: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
})

type ImageOnlyCard = ContentCard & {
  type: 'ImageOnly'
}

export default function ImageOnlyCard({
  contentCard,
  minWidth,
  minHeight,
  style,
}: {
  contentCard: ImageOnlyCard
  minWidth?: number | string
  minHeight?: number | string
  style?: ViewProps['style']
}) {
  const logError = useLogError()
  const isSplitScreen = useMediaQuery({ minWidth: TRANSITION_TO_SPLIT_SCREEN_WIDTH })

  useEffect(() => {
    cardMetrics.logImpression(contentCard)
  }, [contentCard])

  const { url, openURLInWebView, image } = contentCard
  const { blurhash, thumbhash } = contentCard.extras

  const pressHandler = useCallback(() => {
    if (!url) {
      return
    }

    cardMetrics.logClick(contentCard)

    if (openURLInWebView) {
      void openInAppBrowser(url, '', 'tab')
    } else {
      navigateToAppUrl({ navigationUrl: url, onError: logError })
    }
  }, [url, openURLInWebView, contentCard, logError])

  if (contentCard.isControl) {
    // Do not render control cards, log impressions only.
    return null
  }

  return (
    <DesignSystem.SplitCard
      onPress={url ? pressHandler : undefined}
      style={[styles.card, { minWidth, minHeight }, style]}
    >
      <DesignSystem.SplitCard.ImageHeader
        containerStyle={[
          styles.cardHeader,
          {
            aspectRatio:
              contentCard.imageAspectRatio > 0 ? contentCard.imageAspectRatio : undefined,
          },
          isSplitScreen && { minHeight: 200 },
        ]}
      >
        <Image
          source={{ uri: image }}
          contentFit="cover"
          style={styles.image}
          placeholder={{ blurhash, thumbhash }}
          transition={500}
        />
      </DesignSystem.SplitCard.ImageHeader>
    </DesignSystem.SplitCard>
  )
}
